import * as React from "react";
import { Tables } from "./Tables";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { AbsoluteCenter } from "../../AbsoluteCenter";
import { Loader } from "../../Loader";
import { ButtonWithSound, LinkWithSound } from "../../AudioProvider";
import { RoomQuery } from "../RoomProvider/index.generated";
import { useUser } from "../../UserProvider";
import { TournamentFragment } from "../graphql/TournamentFragment.generated";
import { useNumberFormatter } from "../../../hooks/useNumberFormatter";
import { PurchaseTournamentEntryButton } from "../PurchaseTournamentEntryButton";
import { useLeaderboards } from "../../Leaderboards";
import tournamentBg from "./assets/tournament-bg.jpg";
import addTable from "./assets/add_table.png";
import sectionBg from "./assets/tables_bg.png";
import { Link, useHistory } from "react-router-dom";
import { useLocation } from "react-router";
import { InstantTournaments } from "./InstantTournaments";
import { ScheduledTournaments } from "./ScheduledTournaments";
import { MyTournaments } from "./MyTournaments";
import { CurrentInstantTournaments } from "./CurrentInstantTournaments";
import { CurrentScheduledTournaments } from "./CurrentScheduledTournaments";
import { useMatching } from "../MatchingProvider";
import { useMutation } from "../../../hooks/useMutation";
import {
  JoinMatchmakingQueueMutation,
  JoinMatchmakingQueueMutationVariables,
} from "../../../graphql/JoinMatchmakingQueueMutation.generated";
import { loader } from "graphql.macro";
import { gql } from "urql";
import { useSubscription } from "../../../hooks/useSubscription";
import { useGameTable } from "./hooks/useGameTable";
import {getIsLoggedAsGuest} from "../../../guestId";

const MATCHMAKING_FOUND_SUBSCRIPTION = gql`
  subscription OnMatchmakingFound {
    matchmakingFound {
      tableId
      roomId
      gameId
      tableName
    }
  }
`;

const supportedGames = [
  "mpirimpa_el",
  "mpirimpa_cy",
  "tichu",
  "belote",
  "coinche",
  "backgammon_classic",
  "backgammon_portes",
];

const JOIN_MATCHMAKING_QUEUE_MUTATION = loader(
  "../../../graphql/JoinMatchmakingQueueMutation.graphql"
);

const DebouncedTables = ({
  debounce = true,
  children,
}: React.PropsWithChildren<{ debounce?: boolean }>) => {
  const [ready, setReady] = React.useState(!debounce);
  React.useEffect(() => {
    if (!debounce) return;

    const id = window.setTimeout(() => setReady(true), 250);
    return () => window.clearTimeout(id);
  }, [debounce]);

  const { t } = useTranslation();

  if (!ready)
    return (
      <AbsoluteCenter>
        <Loader description={t("loading_tables")} />
      </AbsoluteCenter>
    );
  else return <>{children}</>;
};

function TournamentCompleted({
  joined,
  tournament,
}: {
  joined: boolean;
  tournament: TournamentFragment;
}) {
  const { t } = useTranslation();
  const toggleLeaderboard = useLeaderboards();
  return (
    <AbsoluteCenter>
      <div className="w-2/5 text-center">
        <span className="text-white font-bold block">
          {t("tournament_completed")}
        </span>
        <ButtonWithSound
          className="btn btn-blue-600 btn-sm text-sm rounded mt-2 w-full"
          onClick={() => toggleLeaderboard([tournament.section.id])}
        >
          {t("leaderboards")}
        </ButtonWithSound>
      </div>
    </AbsoluteCenter>
  );
}

function JoinTournament({
  joined,
  tournament,
  children,
}: React.PropsWithChildren<{
  joined: boolean;
  tournament: TournamentFragment;
}>) {
  const numberFormatter = useNumberFormatter();
  const { t } = useTranslation();
  const price = `${numberFormatter.format(tournament.amount.value)} ${t(
    `currency.${tournament.amount.currency.toLowerCase()}`
  )}`;
  return (
    <>
      {children}
      <AbsoluteCenter>
        <div className="w-2/5 text-center p-4 bg-gray-900">
          <span className="text-white font-bold block">
            {joined
              ? t("tournament_join_instructions_already_joined", {
                  price: price,
                  games: tournament.gamesPerPurchase,
                })
              : t("tournament_join_instructions", {
                  price: price,
                  games: tournament.gamesPerPurchase,
                })}
          </span>
          <PurchaseTournamentEntryButton
            className="btn btn-blue-600 btn-sm text-sm rounded mt-2 w-full"
            tournament={tournament}
          >
            {t("purchase")}
          </PurchaseTournamentEntryButton>
        </div>
      </AbsoluteCenter>
    </>
  );
}

function TablesOrJoinTournamentOrExpiredTournament({
  section,
}: {
  section: NonNullable<RoomQuery["room"]>["sections"][0];
}) {
  const tournament =
    section.__typename === "TournamentSection" ? section.tournament : undefined;

  const { user } = useUser();
  const participation = React.useMemo(() => {
    if (tournament === undefined) return undefined;
    const participation = user.tournamentParticipations.find(
      (p) => p.leaderboard.id === tournament.id
    );

    return participation ?? null;
  }, [tournament, user.tournamentParticipations]);

  return tournament !== undefined && !tournament.active ? (
    <TournamentCompleted
      tournament={tournament!}
      joined={participation !== null}
    />
  ) : participation === undefined ||
    (participation?.gamesRemaining ?? 0) > 0 ? (
    <DebouncedTables>
      <Tables
        sectionId={section.id}
        gameType={section.type}
        tournamentId={tournament?.id}
      />
    </DebouncedTables>
  ) : (
    <JoinTournament tournament={tournament!} joined={participation !== null}>
      <DebouncedTables>
        <Tables
          sectionId={section.id}
          gameType={section.type}
          tournamentId={tournament?.id}
        />
      </DebouncedTables>
    </JoinTournament>
  );
}

const GlobalCreateTableButton = ({ sectionId }: { sectionId: string }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const { createTableFlow } = useGameTable(sectionId);

  const handleCreate = async () => {
    setLoading(true);
    try {
      const result = await createTableFlow();
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ButtonWithSound
      onClick={handleCreate}
      className="btn btn-green-500 text-white font-semibold rounded-lg px-4 py-2 leading-none w-full"
      disabled={loading}
    >
      {loading ? t("loading") : t("invitations_title")}
    </ButtonWithSound>
  );
};

export const TablesList = React.memo<{
  sections: Array<NonNullable<RoomQuery["room"]>["sections"][0]>;
  section: NonNullable<RoomQuery["room"]>["sections"][0];
  instantTournament?: boolean;
}>(({ sections, section, instantTournament }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { push } = useHistory();
  const { toggle, isHideTables, setIsInsufficientFunds } = useMatching();
  const [isInQueue, setIsInQueue] = React.useState(false);

  const isSupportedTournament = supportedGames.includes(section.type);

  const [, joinQueue] = useMutation<
    JoinMatchmakingQueueMutation,
    JoinMatchmakingQueueMutationVariables
  >(JOIN_MATCHMAKING_QUEUE_MUTATION);

  const handleMatchFound = React.useCallback(
    (response: any, data: any) => {
      if (data?.matchmakingFound) {
        const { roomId, tableId, gameId } = data.matchmakingFound;

        setIsInQueue(false);
        toggle(false);

        if (tableId && roomId && gameId) {
          push(`/area/${roomId}:${gameId}/${tableId}`);
        }
      }
      return response;
    },
    [toggle]
  );

  useSubscription(
    {
      query: MATCHMAKING_FOUND_SUBSCRIPTION,
      pause: !isInQueue,
    },
    handleMatchFound
  );

  if (instantTournament) {
    if (pathname.includes("instant-tournaments/current")) {
      return <CurrentInstantTournaments />;
    } else if (pathname.includes("scheduled-tournaments/current")) {
      return <CurrentScheduledTournaments />;
    } else if (pathname.includes("instant-tournaments")) {
      return <InstantTournaments />;
    } else if (pathname.includes("scheduled-tournaments")) {
      return <ScheduledTournaments />;
    } else {
      return <MyTournaments />;
    }
  }

  const handleJoinQueue = () => {
    joinQueue({ sectionId: section.id! }).then((data) => {
      if (
        data?.data?.joinMatchmakingQueue?.success ||
        data?.data?.joinMatchmakingQueue?.alreadyInQueue
      ) {
        toggle(true);
        setIsInQueue(true);
      } else if (!data?.data?.joinMatchmakingQueue?.success) {
        if (data?.data?.joinMatchmakingQueue?.insufficientFunds) {
          setIsInsufficientFunds(true);
        }
      }
    });
  };

  return (
    <div className="h-full flex flex-col">
      {sections.length > 1 && (
        <div className="flex flex-shrink-0">
          {sections.map((currSection) => {
            const isActive = currSection === section;
            return (
              <LinkWithSound
                to={`/area/${currSection.id}`}
                key={currSection.id}
                className={classNames(
                  "py-2",
                  "px-4",
                  "text-white",
                  "rounded-t-lg",
                  "font-extrabold",
                  "bg-gray-800",
                  {
                    "bg-gray-900 text-blue-600": isActive,
                  }
                )}
              >
                <div className="flex -mx-1 items-center">
                  <span className="px-1">{t(`game.${currSection.type}`)}</span>
                  <div className="px-1">
                    <div
                      className={classNames("rounded-full", "px-2", "text-xs", {
                        "bg-white text-black": !isActive,
                        "bg-blue-600 text-white": isActive,
                      })}
                    >
                      {currSection.count}
                    </div>
                  </div>
                </div>
              </LinkWithSound>
            );
          })}
        </div>
      )}
      <div className="flex flex-col justify-between gap-6 h-full">
        <div
          className={classNames(
            "flex-1",
            "rounded-b-lg",
            "overflow-hidden",
            isHideTables ? "bg-cover bg-center" : "bg-gray-900",
            {
              "rounded-t-lg": sections.length === 1,
            }
          )}
          style={isHideTables ? { backgroundImage: `url(${sectionBg})` } : {}}
        >
          <div key={section.id} className="h-full relative">
            {isHideTables ? (
              <div
                className="flex flex-col align-middle justify-center h-full"
                style={{
                  width: "520px",
                  margin: "0 auto",
                  textAlign: "center",
                  gap: "12px",
                }}
              >
                <img
                  width={100}
                  height={80}
                  src={addTable}
                  style={{ margin: "0 auto" }}
                />
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-1">
                    <p
                      className="font-semibold text-xl text-white"
                      style={{
                        lineHeight: "32px",
                      }}
                    >
                      {t("matchmaking.select_how_to_play")}
                    </p>
                    <p className="text-white">
                      {t("matchmaking.how_to_play_description")}
                    </p>
                  </div>
                  <div
                    className="flex gap-4"
                    style={{ minWidth: "350px", margin: "0 auto" }}
                  >
                    {
                      !getIsLoggedAsGuest() && <GlobalCreateTableButton sectionId={section.id} />
                    }

                    <ButtonWithSound
                      className="btn btn-blue-600 text-white font-semibold px-4 py-2 rounded-lg w-full"
                      onClick={handleJoinQueue}
                    >
                      {t("matchmaking.quick_play")}
                    </ButtonWithSound>
                  </div>
                </div>
              </div>
            ) : (
              <TablesOrJoinTournamentOrExpiredTournament section={section} />
            )}
          </div>
        </div>
        {isSupportedTournament && (
          <div
            className="relative flex justify-end items-center"
            style={{
              height: "160px",
              color: "white",
              background: "#1A212D",
              paddingRight: "40px",
              backgroundImage: `url(${tournamentBg})`,
              backgroundSize: "cover",
              borderRadius: "1rem",
            }}
          >
            <div className="w-2/5 flex flex-col gap-4 ">
              <div>
                <span className="text-white font-bold block text-2xl">
                  {t("instant_tournament.banner_title")}
                </span>
              </div>
              <div>
                <span className="text-white block text-sm">
                  {t("instant_tournament.banner_text_1")}
                </span>
                <span className="text-white block text-sm">
                  {t("instant_tournament.banner_text_2")}
                </span>
              </div>
            </div>
            <div className=" flex justify-end w-3/12 right-0">
              <Link
                className="btn btn-blue-600 rounded-md shadow-xl text-white gap-2 px-6 py-1 text-center"
                to={`/area/${section?.id?.split(":")[0]}:${
                  section.type
                }/instant-tournaments`}
              >
                {t("instant_tournament.explore_tournaments")}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
